import { camelizeKeys } from 'humps'

import TimeoutView from '@/views/timeout/index.vue'
import LoadingView from '@/views/loading/index.vue'

const buildAppRoutes = (locator) => [
  {
    path: '/plan',
    name: 'Plan',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "checkout" */ '@/views/plan/index.vue')
      ),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "checkout" */ '@/views/checkout/index.vue')
      ),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/vpn-error',
    name: 'vpn-error',
    component: () => lazyLoadView(import('@/views/vpn-error/index.vue')),
    meta: {
      authRequired: false,
    },
  },
  {
    path: '/checkout/add-card/successful',
    name: 'add-card-successful',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "add-card-successful" */ '@/views/checkout/add-card-result/successful/index.vue'
        )
      ),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/checkout/add-card/failure',
    name: 'add-card-failure',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "add-card-failure" */ '@/views/checkout/add-card-result/failure/index.vue'
        )
      ),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/checkout/finance',
    name: 'finance',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "finance" */ '@/views/checkout/finance/index.vue'
        )
      ),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/checkout/kyc-successful',
    name: 'kyc-successful',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "kyc-successful" */ '@/views/checkout/kyc-successful/index.vue'
        )
      ),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/checkout/kyc-failure',
    name: 'kyc-failure',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "kyc-failure" */ '@/views/checkout/kyc-failure/index.vue'
        )
      ),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/auth/checkout/:checkoutId',
    name: 'auth-checkout',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "auth-checkout" */ '@/views/auth-checkout/index.vue'
        )
      ),
    meta: {
      authRequired: false,
    },
  },
  {
    path: '/checkout/:checkoutId',
    name: 'home',
    meta: {
      async beforeResolve(routeTo, _, next) {
        try {
          const store = locator.store
          const logger = locator.logger

          const {
            landing_variant: landingVariant = '',
            pay_the_difference_disclaimer: payTheDifferenceDisclaimer = '',
            id_match_another_user: idMatchAnotherUser = '',
            pay_in_full_value: payInFullValue = '',
            ivr = '',
            payment_plan_content: paymentPlanContent = '',
            payment_fee_popup: paymentFeePopup = '',
          } = routeTo.query

          await Promise.all([
            store.dispatch('auth/resetAuth'),
            store.dispatch('checkout/clearSession'),
            store.dispatch('payment/setFailRedirectionFrom3Ds', false),
          ])

          const applicationVariants = {
            landingVariant,
            payTheDifferenceDisclaimer,
            idMatchAnotherUser,
            payInFullValue,
            ivr,
            paymentPlanContent,
            paymentFeePopup,
          }

          await store.dispatch(
            'application/updateApplicationVariants',
            applicationVariants
          )

          const { checkoutId } = routeTo.params

          try {
            if (store.getters.sessionStatus === 'uncheck') {
              // TODO: Will remove this log after resolve the bug
              logger.debug(
                'Set checkoutID',
                { data: { checkoutId } },
                { category: 'routes', 'order.checkoutId': checkoutId }
              )
              // instead of clearing session on router resolve we will clear it only before fetching new session.
              await store.dispatch('application/clearSession')
              await store.dispatch('session/setCheckoutId', checkoutId)
              await store.dispatch('session/checkSession', { checkoutId })
            }
          } catch (error) {
            logger.error(
              error,
              { checkoutId, category: 'route-checkout' },
              { checkoutId }
            )

            return next({ name: '404', query: { ...routeTo.query } })
          }

          const routerQuery = routeTo.query || {}

          return next({ name: 'login', query: { ...routerQuery, checkoutId } })
        } catch (_) {
          // Redirect to expired page when got any error
          next({ path: '/404' })
        }
      },
    },
  },
  {
    path: '/process-checkout-failure',
    name: 'checkout-failed',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "checkout-failed" */ '@/views/error-page/error-full-screen/index.vue'
        )
      ),
  },
  {
    path: '/process-checkout-success',
    name: 'checkout-success',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "checkout-success" */ '@/views/success-page/success-full-screen/index.vue'
        )
      ),
    meta: {
      beforeResolve(routeTo, _, next) {
        const store = locator.store
        if (!store.getters['checkout/isFinishSuccessfuly']) {
          return next({ name: 'welcome', query: routeTo.query })
        }

        return next()
      },
    },
  },
  {
    path: '/redirect',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "redirect" */ '@/views/redirect/index.vue')
      ),
  },
  {
    path: '/login',
    name: 'login',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "login" */ '@/views/login/index.vue')
      ),
    meta: {
      async beforeResolve(routeTo, routeFrom, next) {
        const store = locator.store

        const isDormantCustomer = store.getters['auth/isDormantCustomer']
        const inAppChallengeRequired =
          store.getters['auth/inAppChallengeRequired']
        // If the user is already logged in
        if (
          store.getters['auth/loggedIn'] &&
          !isDormantCustomer &&
          !inAppChallengeRequired
        ) {
          // Redirect to the home page instead
          return next({ name: 'checkout' })
        }

        return next()
      },
    },
  },
  {
    path: '/ivr',
    name: 'ivr',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "ivr" */ '@/views/ivr/index.vue')
      ),
  },
  {
    path: '/welcome',
    name: 'welcome',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "welcome" */ '@/views/welcome/index.vue')
      ),
  },
  {
    path: '/lean-connect',
    name: 'lean-connect',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "lean" */ '@/views/lean-connect/index.vue')
      ),
  },
  {
    path: '/uqudo-verification',
    name: 'uqudo-verification',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "uqudo" */ '@/views/uqudo-verification/index.vue'
        )
      ),
  },
  {
    path: '/uae-kyc',
    name: 'uae-kyc',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "uae-kyc" */ '@/views/uae-kyc/index.vue')
      ),
  },
  {
    path: '/jump-back',
    name: 'flow-jump-back',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "jump" */ '@/views/flow-jump-point/back.vue'
        )
      ),
  },
  {
    path: '/kyc',
    name: 'kyc',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "kyc" */ '@/views/new-kyc/index.vue')
      ),
    meta: {
      authRequired: false,
      kycFlow: true, // TODO: We will remove this after the new KYC is decoupled from the checkout
      async beforeResolve(routeTo, _, next) {
        const store = locator.store
        const { kyc_session_id: kycSessionId = '' } = routeTo.query

        if (store.getters.kycSessionStatus === 'uncheck') {
          await store.dispatch('kyc/setKycSessionId', kycSessionId)
          await store.dispatch(
            'kyc/setQueryParams',
            camelizeKeys(routeTo.query)
          )
        }

        next()
      },
    },
  },
  {
    path: '/',
    redirect: '/checkout',
  },
  {
    path: '/404',
    name: '404',
    component: () =>
      lazyLoadView(import('@/views/error-page/not-found/index.vue')),
  },
  {
    path: '/401',
    name: '401',
    component: () =>
      lazyLoadView(import('@/views/error-page/not-found/index.vue')),
  },
  // 404 page must be placed at the end !!!
  { path: '*', redirect: '/404' },
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@/views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@/views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: LoadingView,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: TimeoutView,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 60000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}

export { buildAppRoutes }
